/**
 * The people half of `/teams`, at the path it has always had.
 *
 * **It no longer carries a nav item** — M-25, 16 Aug 2026. *Team* and *Teams* sat next to each
 * other in the sidebar, one letter apart, and nothing on either label said which was the person
 * list and which was the org chart. They are one screen now (`people-and-teams.tsx`), reached
 * from ADMINISTRATION → Teams.
 *
 * The route stays, loading the merged screen, because `/team` is in browser histories and in at
 * least one seeded deep link. A path that used to work and now 404s is a worse outcome than two
 * paths onto one screen — which is the arrangement `job-activity` and `job-commitments` already
 * have, for the same reason.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const ACCESS_ROUTES: readonly RouteManifestEntry[] = [
  {
    id: 'team',
    path: '/team',
    entitlement: null,
    entitlementExemption:
      'Team membership is not a module a tenant buys — it is how anybody reaches any module. ' +
      'Gating it on an entitlement would leave a tenant unable to manage the access that every ' +
      'other entitlement depends on. The permission below is the real gate.',
    // Everyone may see who their colleagues are; the screen itself hides the invite form and the
    // role controls from anyone who cannot use them.
    requires: { action: 'read', resource: 'member' },
    authenticated: true,
    load: () => import('../screens/people-and-teams.js'),
  },
  /*
   * The person's own record and preferences.
   *
   * **No `nav` entry, and that is not an omission.** It is reached from the identity control at the
   * foot of the sidebar, which is where every product this one competes with puts it — and adding a
   * tenth link to a list of ten would spend a nav slot on the one destination that never needs
   * finding. `nav` is optional on the manifest and FE-02 only demands a section when one is
   * present, so the gate is satisfied by saying nothing rather than by an exemption.
   *
   * `read me` is the permission every role holds, which is exactly right: this screen is the
   * principal reading themselves, and there is no role that may not.
   */
  {
    id: 'profile',
    path: '/profile',
    entitlement: null,
    entitlementExemption:
      'A person is not a module a tenant buys. Their own record and preferences must stay ' +
      'reachable whatever the agency has or has not bought — the same argument ' +
      '`access.preference.list` makes about the rows this screen edits.',
    requires: { action: 'read', resource: 'me' },
    authenticated: true,
    load: () => import('../screens/profile.js'),
  },
];
